// 登記/編集フォーム（条件付き表示）。CLAUDE.md §6-1。
//  - 親フィールド = 持續性（continuity）。
//  - 「單次(oneoff)」→ 合約期間・下次續約日を非表示。
//  - 「自動續約(auto_renew)」→ 下次續約日を表示し、特殊條款の「自動續約」を自動チェック＆固定。
import { useMemo, useState } from "react";
import {
  Button,
  CheckboxGroup,
  Field,
  SelectInput,
  TextInput,
} from "../../components/ui";
import {
  AMOUNT_TYPES,
  CONTINUITIES,
  CONTRACT_TYPES,
  CURRENCIES,
  SPECIAL_CLAUSES,
} from "../../masters";
import { t } from "../../i18n/zh-TW";
import type { Contract, ContractFormValues } from "../../lib/types";

export type FormAction = "draft" | "submit" | "save" | "resubmit";

export function emptyFormValues(): ContractFormValues {
  return {
    contract_name: "",
    counterparty: "",
    contract_type: "",
    continuity: "",
    amount: "",
    currency: "NTD",
    amount_type: "",
    period_start: "",
    period_end: "",
    special_clauses: [],
    next_renewal_date: "",
  };
}

export function contractToFormValues(c: Contract): ContractFormValues {
  return {
    contract_name: c.contract_name,
    counterparty: c.counterparty,
    contract_type: c.contract_type,
    continuity: c.continuity,
    amount: c.amount === null ? "" : String(c.amount),
    currency: c.currency ?? "NTD",
    amount_type: c.amount_type ?? "",
    period_start: c.period_start ?? "",
    period_end: c.period_end ?? "",
    special_clauses: c.special_clauses,
    next_renewal_date: c.next_renewal_date ?? "",
  };
}

export function ContractForm({
  mode,
  initialValues,
  submitting,
  serverError,
  onAction,
}: {
  mode: "create" | "edit";
  initialValues: ContractFormValues;
  submitting: boolean;
  serverError: string | null;
  onAction: (values: ContractFormValues, action: FormAction) => void;
}) {
  const [values, setValues] = useState<ContractFormValues>(initialValues);
  const [localError, setLocalError] = useState<string | null>(null);

  const isOneoff = values.continuity === "oneoff";
  const isAutoRenew = values.continuity === "auto_renew";
  const showPeriod = values.continuity !== "" && !isOneoff;

  function set<K extends keyof ContractFormValues>(key: K, val: ContractFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  // 持續性の変更ハンドラ：自動續約を選んだら特殊條款側も自動チェック（連動）。
  function onContinuityChange(code: string) {
    setValues((v) => {
      let clauses = v.special_clauses;
      if (code === "auto_renew" && !clauses.includes("auto_renew")) {
        clauses = [...clauses, "auto_renew"];
      }
      const next = { ...v, continuity: code, special_clauses: clauses };
      if (code === "oneoff") {
        next.period_start = "";
        next.period_end = "";
      }
      if (code !== "auto_renew") {
        next.next_renewal_date = "";
      }
      return next;
    });
  }

  const requiredMissing = useMemo(() => {
    const checks: [string, string][] = [
      [values.contract_name.trim(), t.fields.contractName],
      [values.counterparty.trim(), t.fields.counterparty],
      [values.contract_type, t.fields.contractType],
      [values.continuity, t.fields.continuity],
    ];
    return checks.find(([val]) => val === "")?.[1] ?? null;
  }, [values]);

  function handle(action: FormAction) {
    if (requiredMissing) {
      setLocalError(`請填寫「${requiredMissing}」。`);
      return;
    }
    setLocalError(null);
    onAction(values, action);
  }

  const error = localError ?? serverError;

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">{t.form.factSectionHint}</p>

      <Field label={t.fields.contractName} required>
        <TextInput
          value={values.contract_name}
          placeholder={t.placeholders.contractName}
          onChange={(e) => set("contract_name", e.target.value)}
        />
      </Field>

      <Field label={t.fields.counterparty} required>
        <TextInput
          value={values.counterparty}
          placeholder={t.placeholders.counterparty}
          onChange={(e) => set("counterparty", e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t.fields.contractType} required>
          <SelectInput
            options={CONTRACT_TYPES}
            placeholder={t.placeholders.select}
            value={values.contract_type}
            onChange={(e) => set("contract_type", e.target.value)}
          />
        </Field>
        <Field label={t.fields.continuity} required>
          <SelectInput
            options={CONTINUITIES}
            placeholder={t.placeholders.select}
            value={values.continuity}
            onChange={(e) => onContinuityChange(e.target.value)}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label={t.fields.amount}>
          <TextInput
            type="number"
            inputMode="numeric"
            value={values.amount}
            placeholder={t.placeholders.amount}
            onChange={(e) => set("amount", e.target.value)}
          />
        </Field>
        <Field label={t.fields.currency}>
          <SelectInput
            options={CURRENCIES}
            value={values.currency}
            onChange={(e) => set("currency", e.target.value)}
          />
        </Field>
        <Field label={t.fields.amountType}>
          <SelectInput
            options={AMOUNT_TYPES}
            placeholder={t.placeholders.select}
            value={values.amount_type}
            onChange={(e) => set("amount_type", e.target.value)}
          />
        </Field>
      </div>

      {showPeriod && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t.fields.periodStart}>
            <TextInput
              type="date"
              value={values.period_start}
              onChange={(e) => set("period_start", e.target.value)}
            />
          </Field>
          <Field label={t.fields.periodEnd}>
            <TextInput
              type="date"
              value={values.period_end}
              onChange={(e) => set("period_end", e.target.value)}
            />
          </Field>
        </div>
      )}

      {isAutoRenew && (
        <Field label={t.fields.nextRenewalDate}>
          <TextInput
            type="date"
            value={values.next_renewal_date}
            onChange={(e) => set("next_renewal_date", e.target.value)}
          />
        </Field>
      )}

      <Field
        label={t.fields.specialClauses}
        hint={isAutoRenew ? t.form.autoRenewLinked : undefined}
      >
        <CheckboxGroup
          options={SPECIAL_CLAUSES}
          value={values.special_clauses}
          onChange={(next) => set("special_clauses", next)}
          disabledCodes={isAutoRenew ? ["auto_renew"] : []}
        />
      </Field>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        {mode === "create" ? (
          <>
            <Button variant="secondary" disabled={submitting} onClick={() => handle("draft")}>
              {t.common.saveDraft}
            </Button>
            <Button disabled={submitting} onClick={() => handle("submit")}>
              {t.common.submit}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" disabled={submitting} onClick={() => handle("save")}>
              {t.common.save}
            </Button>
            <Button disabled={submitting} onClick={() => handle("resubmit")}>
              {t.common.resubmit}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
